import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Divider,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  alpha,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Visibility,
  VisibilityOff,
  Google,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { AnimatedButton } from './AnimatedButton';
import { gradients } from '../../theme/v2Theme';
import { signInWithEmail, signUpWithEmail, signInWithGoogle, signInWithWallet } from '../../utils/auth';
import { registerHandle, isHandleAvailable } from '../../utils/handleManager';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, onSuccess }) => {
  const [tab, setTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    handle: '',
    displayName: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null); // Clear error on input change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (tab === 0) {
        // Sign In
        await signInWithEmail(formData.email, formData.password);
      } else {
        // Sign Up
        if (!formData.handle) {
          setError('Please enter a handle');
          setLoading(false);
          return;
        }

        // Check if handle is available
        const available = await isHandleAvailable(formData.handle);
        if (!available) {
          setError('This handle is already taken. Please choose another one.');
          setLoading(false);
          return;
        }

        // Create Firebase Auth account
        const user = await signUpWithEmail(formData.email, formData.password);

        if (user?.uid) {
          // Register handle in Realtime Database
          await registerHandle(formData.handle, user.uid);
        }
      }

      onSuccess?.();
      onClose();

      // Clear form
      setFormData({
        email: '',
        password: '',
        handle: '',
        displayName: '',
      });
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      await signInWithGoogle();
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletConnect = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithWallet();

      // If it's a new user and we're on the sign-up tab, register the handle
      if (result.isNewUser && tab === 1) {
        if (!formData.handle) {
          setError('Please enter a handle to complete wallet sign-up');
          setLoading(false);
          return;
        }

        // Check if handle is available
        const available = await isHandleAvailable(formData.handle);
        if (!available) {
          setError('This handle is already taken. Please choose another one.');
          setLoading(false);
          return;
        }

        if (result.user?.uid) {
          // Register handle in Realtime Database
          await registerHandle(formData.handle, result.user.uid);
        }
      }

      onSuccess?.();
      onClose();

      // Clear form
      setFormData({
        email: '',
        password: '',
        handle: '',
        displayName: '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          maxWidth: 480,
          width: '100%',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={800}>
            {tab === 0 ? 'Welcome Back' : 'Create Account'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="Sign In" />
          <Tab label="Sign Up" />
        </Tabs>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Social Login Buttons */}
            <Stack spacing={2}>
              <Button
                fullWidth
                startIcon={<Google />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  borderRadius: 1.5,
                  p: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  border: '2px solid',
                  borderColor: alpha('#000', 0.1),
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    background: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Continue with Google
              </Button>
              <Button
                fullWidth
                startIcon={<AccountBalanceWallet />}
                onClick={handleWalletConnect}
                disabled={loading}
                sx={{
                  borderRadius: 1.5,
                  p: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  border: '2px solid',
                  borderColor: alpha('#000', 0.1),
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    background: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Connect Wallet
              </Button>
            </Stack>

            <Divider>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>

            {/* Sign Up Fields */}
            {tab === 1 && (
              <>
                <TextField
                  fullWidth
                  label="Display Name"
                  placeholder="Satoshi Nakamoto"
                  value={formData.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Choose Handle"
                  placeholder="satoshi"
                  value={formData.handle}
                  onChange={(e) => handleChange('handle', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography fontWeight={700}>@</Typography>
                      </InputAdornment>
                    ),
                  }}
                  required
                  helperText="This will be your unique addrs.to/handle"
                />
              </>
            )}

            {/* Email & Password */}
            <TextField
              fullWidth
              type="email"
              label="Email"
              placeholder="satoshi@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />

            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {tab === 0 && (
              <Box textAlign="right">
                <Button
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Forgot Password?
                </Button>
              </Box>
            )}

            <AnimatedButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{
                background: gradients.primary,
                py: 1.5,
                '&:hover': {
                  background: gradients.primary,
                  opacity: 0.9,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                tab === 0 ? 'Sign In' : 'Create Account'
              )}
            </AnimatedButton>

            {tab === 1 && (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                By signing up, you agree to our Terms of Service and Privacy Policy
              </Typography>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
